import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleMap, Marker, DirectionsRenderer, useJsApiLoader } from '@react-google-maps/api';
import { auth, db } from '../firebase';
import { collection, doc, getDoc, getDocs, onSnapshot, query, setDoc, updateDoc, where } from 'firebase/firestore';

const createBusIcon = (size = 32) => {
  if (!window.google?.maps) {
    return null;
  }

  // Match Track.js bus marker style exactly for consistency.
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
      <path d="M256 0C390.4 0 480 35.2 480 80V96l0 32c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32l0 160c0 17.7-14.3 32-32 32v32c0 17.7-14.3 32-32 32H384c-17.7 0-32-14.3-32-32V448H160v32c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32l0-32c-17.7 0-32-14.3-32-32l0-160c-17.7 0-32-14.3-32-32V160c0-17.7 14.3-32 32-32h0V96h0V80C32 35.2 121.6 0 256 0zM96 160v96c0 17.7 14.3 32 32 32H240V128H128c-17.7 0-32 14.3-32 32zM272 288H384c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H272V288zM112 400c17.7 0 32-14.3 32-32s-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32zm288 0c17.7 0 32-14.3 32-32s-14.3-32-32-32s-32 14.3-32 32s14.3 32 32 32zM352 80c0-8.8-7.2-16-16-16H176c-8.8 0-16 7.2-16 16s7.2 16 16 16H336c8.8 0 16-7.2 16-16z" fill="#1a1a1a" stroke="#000000" stroke-width="1"/>
    </svg>
  `;

  return {
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2)
  };
};

const mapLibraries = ['places', 'marker'];
const googleMapId = (process.env.REACT_APP_GOOGLE_MAP_ID || '').trim();

const createCircleMarkerContent = (fillColor, strokeColor) => {
  const markerElement = document.createElement('div');
  markerElement.style.width = '18px';
  markerElement.style.height = '18px';
  markerElement.style.borderRadius = '50%';
  markerElement.style.backgroundColor = fillColor;
  markerElement.style.border = `2px solid ${strokeColor}`;
  markerElement.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.25)';
  return markerElement;
};

const createBusMarkerContent = (iconSize, labelText) => {
  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.flexDirection = 'column';
  wrapper.style.alignItems = 'center';
  wrapper.style.gap = '4px';

  const iconImage = document.createElement('img');
  iconImage.src = createBusIcon(iconSize)?.url || '';
  iconImage.width = iconSize;
  iconImage.height = iconSize;
  iconImage.alt = 'Bus';
  wrapper.appendChild(iconImage);

  if (labelText) {
    const label = document.createElement('div');
    label.textContent = labelText;
    label.style.padding = '2px 6px';
    label.style.borderRadius = '10px';
    label.style.backgroundColor = '#ffffff';
    label.style.border = '1px solid #d1d5db';
    label.style.fontSize = '12px';
    label.style.fontWeight = '700';
    label.style.color = '#111827';
    label.style.whiteSpace = 'nowrap';
    wrapper.appendChild(label);
  }

  return wrapper;
};

const AdvancedMapMarker = ({ map, position, title, buildContent }) => {
  const markerRef = React.useRef(null);

  useEffect(() => {
    if (!map || !position || !window.google?.maps?.marker?.AdvancedMarkerElement) {
      return undefined;
    }

    const marker = new window.google.maps.marker.AdvancedMarkerElement({
      map,
      position,
      title,
      content: buildContent ? buildContent() : null
    });
    markerRef.current = marker;

    return () => {
      marker.map = null;
      markerRef.current = null;
    };
  // Recreate only when map or content-builder identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, buildContent]);

  useEffect(() => {
    if (markerRef.current && position) {
      markerRef.current.position = position;
    }
  }, [position]);

  useEffect(() => {
    if (markerRef.current && title != null) {
      markerRef.current.title = title;
    }
  }, [title]);

  return null;
};

const toTripTypeLabel = (tripType) => {
  if (tripType === 'AM') return 'School Trip';
  if (tripType === 'PM') return 'Home Trip';
  if (tripType === 'FIELD_TRIP') return 'Field Trip';
  return tripType || 'Trip';
};

const parseLocation = (location) => {
  if (!location) return null;

  const readCoordinate = (obj, keys) => {
    for (const key of keys) {
      const rawValue = obj?.[key];
      if (typeof rawValue === 'function') {
        const fnValue = Number(rawValue());
        if (Number.isFinite(fnValue)) return fnValue;
        continue;
      }

      const value = Number(rawValue);
      if (Number.isFinite(value)) return value;
    }
    return null;
  };

  const lat = readCoordinate(location, ['lat', 'latitude', '_lat']);
  const lng = readCoordinate(location, ['lng', 'lon', 'long', 'longitude', '_long', '_lng']);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
};

const findStudentWaypoint = (route, student) => {
  const studentId = student?.id != null ? String(student.id) : '';
  if (!studentId) return null;

  return (route?.studentSequence || []).find((waypoint) => {
    const waypointStudentId =
      waypoint?.id ??
      waypoint?.studentId ??
      waypoint?.student?.id ??
      waypoint?.studentRef ??
      null;
    return waypoint?.location && waypointStudentId != null && String(waypointStudentId) === studentId;
  }) || null;
};

const parseRouteDeparture = (timeString, options = {}) => {
  const { rollToNextDay = true } = options;
  if (!timeString) return null;
  const timeParts = timeString.split(':').map(Number);
  if (timeParts.length < 2 || Number.isNaN(timeParts[0]) || Number.isNaN(timeParts[1])) {
    return null;
  }

  const now = new Date();
  const departure = new Date(now);
  departure.setHours(timeParts[0], timeParts[1], 0, 0);
  if (rollToNextDay && departure.getTime() < now.getTime() - 60 * 1000) {
    departure.setDate(departure.getDate() + 1);
  }
  return departure;
};

const weekdayNameByIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const computeScheduledDeparture = (roster, routeTime) => {
  if (!routeTime) return null;

  const [hours, minutes] = String(routeTime).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;

  const now = new Date();
  const startDate = roster?.date ? new Date(roster.date) : null;
  const endDate = roster?.endDate ? new Date(roster.endDate) : null;
  const frequency = roster?.frequency || 'once';

  const withRouteTime = (baseDate) => {
    const candidate = new Date(baseDate);
    candidate.setHours(hours, minutes, 0, 0);
    return candidate;
  };

  if (frequency === 'once') {
    return withRouteTime(startDate || now);
  }

  if (frequency === 'daily') {
    const anchor = startDate && startDate.getTime() > now.getTime() ? startDate : now;
    const candidate = withRouteTime(anchor);
    if (candidate.getTime() < now.getTime() - 60 * 1000) {
      candidate.setDate(candidate.getDate() + 1);
    }
    if (endDate && candidate.getTime() > withRouteTime(endDate).getTime()) {
      return null;
    }
    return candidate;
  }

  if (frequency === 'weekdays') {
    const allowedWeekdays = new Set(
      Array.isArray(roster?.weekdays) && roster.weekdays.length
        ? roster.weekdays
        : ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    );
    const anchor = startDate && startDate.getTime() > now.getTime() ? startDate : now;
    const candidate = withRouteTime(anchor);
    if (candidate.getTime() < now.getTime() - 60 * 1000) {
      candidate.setDate(candidate.getDate() + 1);
    }

    for (let i = 0; i < 14; i += 1) {
      const dayName = weekdayNameByIndex[candidate.getDay()];
      const inRange = !endDate || candidate.getTime() <= withRouteTime(endDate).getTime();
      if (allowedWeekdays.has(dayName) && inRange) {
        return candidate;
      }
      candidate.setDate(candidate.getDate() + 1);
      candidate.setHours(hours, minutes, 0, 0);
    }
    return null;
  }

  return parseRouteDeparture(routeTime);
};

const formatArrival = (arrivalDate) => {
  if (!arrivalDate) return 'N/A';
  return arrivalDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatDeparture = (departureDate) => {
  if (!departureDate) return 'N/A';
  return departureDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatTripDate = (tripDate) => {
  if (!tripDate || Number.isNaN(tripDate.getTime())) return 'N/A';
  return tripDate.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDistance = (meters) => {
  if (!Number.isFinite(meters) || meters <= 0) return null;
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

const ParentDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [parentProfile, setParentProfile] = useState(null);
  const [students, setStudents] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [devices, setDevices] = useState([]);
  const [linkageWarning, setLinkageWarning] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [focusedDirections, setFocusedDirections] = useState(null);
  const [driverProfile, setDriverProfile] = useState(null);
  const [tripMap, setTripMap] = useState(null);
  const [resolvedDestinationName, setResolvedDestinationName] = useState('');
  const [plannedTripMetrics, setPlannedTripMetrics] = useState({
    distanceText: null,
    etaMinutes: null,
    arrivalAt: null
  });
  const [liveTripMetrics, setLiveTripMetrics] = useState({
    distanceText: null,
    etaMinutes: null,
    arrivalAt: null
  });
  const { isLoaded: isMapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: mapLibraries
  });

  useEffect(() => {
    const loadParentProfile = async () => {
      try {
        setError('');
        setLinkageWarning('');
        const user = auth.currentUser;
        if (!user) {
          setLoading(false);
          return;
        }

        const lookupFailures = [];
        let userData = {};
        try {
          const userDocSnapshot = await getDoc(doc(db, 'users', user.uid));
          userData = userDocSnapshot.exists() ? userDocSnapshot.data() : {};
        } catch (userDocErr) {
          lookupFailures.push(`users/${user.uid}: ${userDocErr.code || userDocErr.message}`);
        }

        let profile = null;
        let profileFoundViaFallback = false;

        try {
          const parentByUserIdQuery = query(collection(db, 'parents'), where('userId', '==', user.uid));
          const parentByUserIdSnapshot = await getDocs(parentByUserIdQuery);
          if (!parentByUserIdSnapshot.empty) {
            profile = {
              id: parentByUserIdSnapshot.docs[0].id,
              ...parentByUserIdSnapshot.docs[0].data()
            };
          }
        } catch (parentByUserIdErr) {
          lookupFailures.push(`parents.userId lookup: ${parentByUserIdErr.code || parentByUserIdErr.message}`);
        }

        // Fallback for legacy/missing userId links: use users.parentRef or users.parentId.
        if (!profile && userData?.parentRef) {
          try {
            const parentRefSnapshot = await getDoc(doc(db, 'parents', userData.parentRef));
            if (parentRefSnapshot.exists()) {
              profile = { id: parentRefSnapshot.id, ...parentRefSnapshot.data() };
              profileFoundViaFallback = true;
            }
          } catch (parentRefErr) {
            lookupFailures.push(`parents/${userData.parentRef}: ${parentRefErr.code || parentRefErr.message}`);
          }
        }

        if (!profile && userData?.parentId) {
          try {
            const parentByParentIdQuery = query(collection(db, 'parents'), where('parentId', '==', userData.parentId));
            const parentByParentIdSnapshot = await getDocs(parentByParentIdQuery);
            if (!parentByParentIdSnapshot.empty) {
              profile = {
                id: parentByParentIdSnapshot.docs[0].id,
                ...parentByParentIdSnapshot.docs[0].data()
              };
              profileFoundViaFallback = true;
            }
          } catch (parentByParentIdErr) {
            lookupFailures.push(`parents.parentId lookup: ${parentByParentIdErr.code || parentByParentIdErr.message}`);
          }
        }

        // Last-resort fallback for migrated accounts: match by auth email.
        if (!profile && user.email) {
          try {
            const parentByEmailQuery = query(collection(db, 'parents'), where('email', '==', user.email));
            const parentByEmailSnapshot = await getDocs(parentByEmailQuery);
            if (!parentByEmailSnapshot.empty) {
              profile = {
                id: parentByEmailSnapshot.docs[0].id,
                ...parentByEmailSnapshot.docs[0].data()
              };
              profileFoundViaFallback = true;
            }
          } catch (parentByEmailErr) {
            lookupFailures.push(`parents.email lookup: ${parentByEmailErr.code || parentByEmailErr.message}`);
          }
        }

        // One-time self-heal: if fallback found the correct parent record, persist direct linkage.
        if (profile && profileFoundViaFallback && profile.userId !== user.uid) {
          try {
            await updateDoc(doc(db, 'parents', profile.id), {
              userId: user.uid,
              activationStatus: profile.activationStatus || 'active',
              updatedAt: new Date().toISOString()
            });
            profile = { ...profile, userId: user.uid };
          } catch (linkErr) {
            // Do not block dashboard rendering if rules still reject self-heal.
            setLinkageWarning('Parent profile loaded via fallback. Direct account link update is pending.');
          }
        }

        // Keep users/{uid} linkage fields in sync so lookups stay stable.
        if (profile && (!userData?.parentId || !userData?.parentRef)) {
          try {
            await setDoc(
              doc(db, 'users', user.uid),
              {
                parentId: profile.parentId || null,
                parentRef: profile.id,
                updatedAt: new Date().toISOString()
              },
              { merge: true }
            );
          } catch (userLinkErr) {
            setLinkageWarning((prev) =>
              prev || 'Parent profile loaded, but user linkage fields could not be updated yet.'
            );
          }
        }

        if (!profile && lookupFailures.length > 0) {
          setError(`Failed to load parent profile. ${lookupFailures[0]}`);
        }

        setParentProfile(profile);
      } catch (err) {
        setError(`Failed to load parent profile. ${err.code || err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    loadParentProfile();
  }, []);

  useEffect(() => {
    if (!parentProfile?.id) {
      return undefined;
    }

    const parentRef = doc(db, 'parents', parentProfile.id);
    const unsubParentProfile = onSnapshot(parentRef, (snapshot) => {
      if (!snapshot.exists()) return;
      setParentProfile((prev) => ({
        ...(prev || {}),
        id: snapshot.id,
        ...snapshot.data()
      }));
    });

    return () => {
      unsubParentProfile();
    };
  }, [parentProfile?.id]);

  useEffect(() => {
    if (!parentProfile?.parentId) {
      return undefined;
    }

    const studentsQuery = query(
      collection(db, 'students'),
      where('parentId', '==', parentProfile.parentId)
    );

    const unsubStudents = onSnapshot(studentsQuery, (snapshot) => {
      setStudents(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    });

    const unsubRosters = onSnapshot(collection(db, 'rosters'), (snapshot) => {
      setRosters(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    });

    const unsubRoutes = onSnapshot(collection(db, 'optimizedRoutes'), (snapshot) => {
      setRoutes(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    });

    const unsubVehicles = onSnapshot(collection(db, 'vehicles'), (snapshot) => {
      setVehicles(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    });

    const unsubDevices = onSnapshot(collection(db, 'devices'), (snapshot) => {
      setDevices(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    });

    return () => {
      unsubStudents();
      unsubRosters();
      unsubRoutes();
      unsubVehicles();
      unsubDevices();
    };
  }, [parentProfile?.parentId]);

  const childTransportCards = useMemo(() => {
    const parentDropoffLocation = parseLocation(parentProfile?.location);
    const parentDropoffName =
      parentProfile?.location?.placeName ||
      parentProfile?.location?.name ||
      parentProfile?.placeName ||
      null;

    return students.map((student) => {
      const roster = rosters.find((rosterItem) =>
        Array.isArray(rosterItem.students) && rosterItem.students.includes(student.id)
      );

      const route = roster
        ? routes.find((routeItem) => routeItem.rosterId === roster.id && routeItem.status !== 'archived')
        : null;

      const attendance = roster?.attendance?.[student.id] || {};
      const boarded = Boolean(attendance.boarded);
      const droppedOff = Boolean(attendance.alighted) && !boarded;

      const bus = route?.busId ? vehicles.find((vehicle) => vehicle.id === route.busId) : null;
      const assignedDeviceId = bus?.assignedDevice?.id || bus?.assignedDevice || bus?.deviceId || null;

      const device = bus
        ? devices.find(
            (deviceItem) =>
              deviceItem.vehicleId === bus.id ||
              deviceItem.id === assignedDeviceId ||
              deviceItem.imeiNumber === assignedDeviceId
          )
        : null;

      const lat = device?.lat !== undefined ? parseFloat(device.lat) : NaN;
      const lng = device?.lon !== undefined ? parseFloat(device.lon) : NaN;
      const hasLiveLocation = !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0;

      const routeDeparture = parseRouteDeparture(route?.time);
      const routeDepartureToday = parseRouteDeparture(route?.time, { rollToNextDay: false });
      const totalDurationSeconds = route?.routeMetrics?.totalDurationWithWait || 0;
      const arrivalAt = routeDeparture ? new Date(routeDeparture.getTime() + totalDurationSeconds * 1000) : null;
      const etaMinutes = arrivalAt ? Math.max(0, Math.ceil((arrivalAt.getTime() - Date.now()) / 60000)) : null;

      const studentWaypoint = findStudentWaypoint(route, student);
      const childDropoffLocation = parseLocation(studentWaypoint?.location);
      const childDropoffName =
        studentWaypoint?.location?.placeName ||
        studentWaypoint?.location?.name ||
        route?.endLocation?.placeName ||
        route?.endLocation?.name ||
        'Destination';

      const origin = parseLocation(route?.startLocation);
      const end = parseLocation(route?.endLocation);
      const isHomeTrip = route?.tripType === 'PM';
      const endName = route?.endLocation?.placeName || route?.endLocation?.name || 'Destination';
      const destination = isHomeTrip
        ? (parentDropoffLocation || childDropoffLocation || end)
        : end;
      const destinationName = isHomeTrip
        ? (parentDropoffName || (childDropoffLocation ? childDropoffName : endName))
        : endName;

      let status = 'NoActiveTrip';
      if (droppedOff) {
        status = 'DroppedOff';
      } else if (boarded) {
        status = 'BoardedAndEnRoute';
      } else if (route) {
        status = 'NotBoarded';
      }

      return {
        student,
        roster,
        route,
        bus,
        device,
        status,
        hasLiveLocation,
        liveLocation: hasLiveLocation ? { lat, lng } : null,
        tripLabel: toTripTypeLabel(route?.tripType),
        routeDeparture,
        routeDepartureToday,
        arrivalAt,
        etaMinutes,
        origin,
        destination,
        destinationName,
        speedKph: Number.isFinite(Number(device?.vehicleSpeed)) ? Number(device.vehicleSpeed) : 0
      };
    });
  }, [students, rosters, routes, vehicles, devices, parentProfile]);

  useEffect(() => {
    if (!childTransportCards.length) {
      setSelectedStudentId('');
      return;
    }

    if (selectedStudentId && childTransportCards.some((card) => card.student.id === selectedStudentId)) {
      return;
    }

    const now = Date.now();
    const boardedFirst = childTransportCards.find((card) => card.status === 'BoardedAndEnRoute' && card.route);
    const nearestUpcoming = childTransportCards
      .filter((card) => card.routeDeparture && card.routeDeparture.getTime() >= now)
      .sort((a, b) => a.routeDeparture.getTime() - b.routeDeparture.getTime())[0];
    const alphabeticalFirst = [...childTransportCards].sort((a, b) =>
      `${a.student.firstName} ${a.student.lastName}`.localeCompare(`${b.student.firstName} ${b.student.lastName}`)
    )[0];

    setSelectedStudentId((boardedFirst || nearestUpcoming || alphabeticalFirst).student.id);
  }, [childTransportCards, selectedStudentId]);

  const focusedCard = useMemo(() => {
    if (!childTransportCards.length) return null;
    return childTransportCards.find((card) => card.student.id === selectedStudentId) || childTransportCards[0];
  }, [childTransportCards, selectedStudentId]);

  const upcomingTripsForFocusedChild = useMemo(() => {
    const student = focusedCard?.student;
    if (!student?.id) return [];

    const studentId = student.id;
    const studentRosters = rosters.filter(
      (roster) => Array.isArray(roster.students) && roster.students.includes(studentId)
    );
    if (!studentRosters.length) return [];

    const studentRosterIds = new Set(studentRosters.map((roster) => roster.id));
    const now = Date.now();

    return routes
      .filter((route) => studentRosterIds.has(route.rosterId) && route.status !== 'archived')
      .map((route) => {
        const roster = studentRosters.find((rosterItem) => rosterItem.id === route.rosterId);
        const attendance = roster?.attendance?.[studentId] || {};
        const boarded = Boolean(attendance.boarded);
        const droppedOff = Boolean(attendance.alighted) && !boarded;
        const scheduledDeparture = computeScheduledDeparture(roster, route?.time);
        const isUpcomingByTime = scheduledDeparture ? scheduledDeparture.getTime() >= now : true;
        const isUpcomingByStatus = !boarded && !droppedOff;
        if (!isUpcomingByStatus && !isUpcomingByTime) return null;

        const studentWaypoint = findStudentWaypoint(route, student);
        const childDropoffLocation = parseLocation(studentWaypoint?.location);
        const parentDropoffName =
          parentProfile?.location?.placeName ||
          parentProfile?.location?.name ||
          parentProfile?.placeName ||
          null;
        const endName = route?.endLocation?.placeName || route?.endLocation?.name || 'Destination';
        const childDropoffName =
          studentWaypoint?.location?.placeName ||
          studentWaypoint?.location?.name ||
          endName;
        const isHomeTrip = route?.tripType === 'PM';
        const destinationName = isHomeTrip
          ? (parentDropoffName || (childDropoffLocation ? childDropoffName : endName))
          : endName;

        return {
          id: `${studentId}-${route.id}`,
          tripLabel: toTripTypeLabel(route?.tripType),
          destinationName,
          tripDate: scheduledDeparture,
          departureAt: scheduledDeparture,
          routeName: route?.name || 'Route'
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (!a.departureAt && !b.departureAt) return 0;
        if (!a.departureAt) return 1;
        if (!b.departureAt) return -1;
        return a.departureAt.getTime() - b.departureAt.getTime();
      });
  }, [focusedCard?.student, rosters, routes, parentProfile]);

  const hasActiveTrip = Boolean(focusedCard?.route && focusedCard.status === 'BoardedAndEnRoute');
  const displayedDistanceText = liveTripMetrics.distanceText ?? plannedTripMetrics.distanceText ?? null;
  const displayedEtaMinutes = liveTripMetrics.etaMinutes ?? plannedTripMetrics.etaMinutes ?? focusedCard?.etaMinutes ?? null;
  const displayedArrivalAt = liveTripMetrics.arrivalAt ?? plannedTripMetrics.arrivalAt ?? focusedCard?.arrivalAt ?? null;
  const displayedDestinationName = resolvedDestinationName || focusedCard?.destinationName || 'Destination';
  const _originLat = focusedCard?.origin?.lat ?? null;
  const _originLng = focusedCard?.origin?.lng ?? null;
  const focusedOrigin = useMemo(
    () => (_originLat != null && _originLng != null ? { lat: _originLat, lng: _originLng } : null),
    [_originLat, _originLng]
  );

  const _destLat = focusedCard?.destination?.lat ?? null;
  const _destLng = focusedCard?.destination?.lng ?? null;
  const focusedDestination = useMemo(
    () => (_destLat != null && _destLng != null ? { lat: _destLat, lng: _destLng } : null),
    [_destLat, _destLng]
  );

  const _liveLat = focusedCard?.liveLocation?.lat ?? null;
  const _liveLng = focusedCard?.liveLocation?.lng ?? null;
  const focusedLiveLocation = useMemo(
    () => (_liveLat != null && _liveLng != null ? { lat: _liveLat, lng: _liveLng } : null),
    [_liveLat, _liveLng]
  );

  const focusedDestinationName = focusedCard?.destinationName || '';
  const focusedStatus = focusedCard?.status || null;
  const focusedRouteDepartureMs = focusedCard?.routeDeparture ? focusedCard.routeDeparture.getTime() : null;
  const focusedRouteDepartureTodayMs = focusedCard?.routeDepartureToday ? focusedCard.routeDepartureToday.getTime() : null;
  const resolvedDriverName = focusedCard?.bus?.assignedDriver?.name || driverProfile?.name || 'Not assigned';
  const resolvedDriverPhoto =
    focusedCard?.bus?.assignedDriver?.profilePicture ||
    driverProfile?.profilePicture ||
    null;
  const resolvedDriverInitial = (resolvedDriverName || 'D').charAt(0).toUpperCase();
  const mapsReady = Boolean(isMapsLoaded && window.google?.maps);
  const useAdvancedMarkers = Boolean(mapsReady && googleMapId && window.google?.maps?.marker?.AdvancedMarkerElement);
  const circleMarkerPath =
    window.google?.maps?.SymbolPath?.CIRCLE ||
    'M 0,0 m -1,0 a 1,1 0 1,0 2,0 a 1,1 0 1,0 -2,0';
  const buildOriginMarkerContent = useCallback(() => createCircleMarkerContent('#34D399', '#1B9A59'), []);
  const buildDestMarkerContent = useCallback(() => createCircleMarkerContent('#EF4444', '#B91C1C'), []);
  const focusedStudentName = `${focusedCard?.student?.firstName || ''} ${focusedCard?.student?.lastName || ''}`.trim();
  const buildBusMarkerContent = useCallback(
    () => createBusMarkerContent(38, focusedStudentName || 'Bus'),
    [focusedStudentName]
  );

  const googleMapOptions = useMemo(() => (googleMapId ? { mapId: googleMapId } : undefined), []);
  const mapContainerStyle = useMemo(() => ({ width: '100%', height: '420px' }), []);
  const directionsRendererOptions = useMemo(() => ({
    suppressMarkers: true,
    polylineOptions: { strokeColor: '#0f766e', strokeOpacity: 0.95, strokeWeight: 5 }
  }), []);

  const handleTripMapLoad = useCallback((mapInstance) => {
    setTripMap(mapInstance);
  }, []);

  const handleTripMapUnmount = useCallback(() => {
    setTripMap(null);
  }, []);

  const mapCenter = useMemo(() => {
    if (focusedOrigin && focusedDestination) {
      return {
        lat: (focusedOrigin.lat + focusedDestination.lat) / 2,
        lng: (focusedOrigin.lng + focusedDestination.lng) / 2
      };
    }
    if (focusedDestination) return focusedDestination;
    if (focusedOrigin) return focusedOrigin;
    if (focusedLiveLocation) return focusedLiveLocation;
    return { lat: 0, lng: 0 };
  }, [focusedOrigin, focusedDestination, focusedLiveLocation]);

  useEffect(() => {
    const loadDirections = async () => {
      if (!focusedOrigin || !focusedDestination || !window.google?.maps) {
        setFocusedDirections(null);
        setPlannedTripMetrics({
          distanceText: null,
          etaMinutes: null,
          arrivalAt: null
        });
        return;
      }

      const origin = focusedOrigin;
      const destination = focusedDestination;
      if (origin.lat === destination.lat && origin.lng === destination.lng) {
        setFocusedDirections(null);
        setPlannedTripMetrics({
          distanceText: null,
          etaMinutes: 0,
          arrivalAt: new Date(focusedRouteDepartureMs || Date.now())
        });
        return;
      }

      try {
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin,
            destination,
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (result, status) => {
            if (status === 'OK' && result?.routes?.[0]?.legs?.length) {
              setFocusedDirections(result);
              const totalDurationSeconds = result.routes[0].legs.reduce(
                (total, leg) => total + (leg.duration_in_traffic?.value || leg.duration?.value || 0),
                0
              );
              const totalDistanceMeters = result.routes[0].legs.reduce(
                (total, leg) => total + (leg.distance?.value || 0),
                0
              );
              let baseStartTimeMs = focusedRouteDepartureTodayMs || focusedRouteDepartureMs || Date.now();
              if (focusedStatus === 'BoardedAndEnRoute' && baseStartTimeMs < Date.now()) {
                // For active trips that have already started, use "now" as the effective baseline.
                baseStartTimeMs = Date.now();
              }
              const arrivalAt = new Date(baseStartTimeMs + totalDurationSeconds * 1000);
              const etaMinutes = Math.max(0, Math.ceil(totalDurationSeconds / 60));
              setPlannedTripMetrics({
                distanceText: formatDistance(totalDistanceMeters),
                etaMinutes,
                arrivalAt
              });
            } else {
              setFocusedDirections(null);
              setPlannedTripMetrics({
                distanceText: null,
                etaMinutes: null,
                arrivalAt: null
              });
            }
          }
        );
      } catch (directionsErr) {
        setFocusedDirections(null);
        setPlannedTripMetrics({
          distanceText: null,
          etaMinutes: null,
          arrivalAt: null
        });
      }
    };

    loadDirections();
  }, [
    mapsReady,
    focusedOrigin,
    focusedDestination,
    focusedRouteDepartureMs,
    focusedRouteDepartureTodayMs,
    focusedStatus
  ]);

  useEffect(() => {
    if (!mapsReady || !focusedLiveLocation || !focusedDestination || focusedStatus !== 'BoardedAndEnRoute') {
      setLiveTripMetrics({ distanceText: null, etaMinutes: null, arrivalAt: null });
      return;
    }

    if (focusedLiveLocation.lat === focusedDestination.lat && focusedLiveLocation.lng === focusedDestination.lng) {
      setLiveTripMetrics({ distanceText: null, etaMinutes: 0, arrivalAt: new Date() });
      return;
    }

    let cancelled = false;
    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: focusedLiveLocation,
        destination: focusedDestination,
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (cancelled) return;
        if (status === 'OK' && result?.routes?.[0]?.legs?.length) {
          const durationSec = result.routes[0].legs.reduce(
            (sum, leg) => sum + (leg.duration_in_traffic?.value || leg.duration?.value || 0),
            0
          );
          const distanceM = result.routes[0].legs.reduce(
            (sum, leg) => sum + (leg.distance?.value || 0),
            0
          );
          setLiveTripMetrics({
            distanceText: formatDistance(distanceM),
            etaMinutes: Math.max(0, Math.ceil(durationSec / 60)),
            arrivalAt: new Date(Date.now() + durationSec * 1000)
          });
        }
      }
    );

    return () => { cancelled = true; };
  }, [mapsReady, focusedLiveLocation, focusedDestination, focusedStatus]);

  useEffect(() => {
    const loadDriverProfile = async () => {
      const assignedDriverId = focusedCard?.bus?.assignedDriver?.id;
      if (!assignedDriverId) {
        setDriverProfile(null);
        return;
      }

      try {
        const driverSnapshot = await getDoc(doc(db, 'users', assignedDriverId));
        if (driverSnapshot.exists()) {
          setDriverProfile(driverSnapshot.data());
          return;
        }

        // Some environments store users under generated doc IDs and keep auth uid in the uid field.
        const userByUidSnapshot = await getDocs(query(collection(db, 'users'), where('uid', '==', assignedDriverId)));
        if (!userByUidSnapshot.empty) {
          setDriverProfile(userByUidSnapshot.docs[0].data());
          return;
        }

        setDriverProfile(null);
      } catch {
        setDriverProfile(null);
      }
    };
    loadDriverProfile();
  }, [focusedCard?.bus?.assignedDriver?.id]);

  useEffect(() => {
    let cancelled = false;

    const fallbackName = focusedDestinationName || '';
    setResolvedDestinationName(fallbackName);

    const resolveDestinationName = async () => {
      if (!mapsReady || !window.google?.maps?.Geocoder || !focusedDestination) {
        return;
      }

      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: focusedDestination }, (results, status) => {
          if (cancelled) return;
          if (status === 'OK' && results?.[0]?.formatted_address) {
            setResolvedDestinationName(results[0].formatted_address);
          }
        });
      } catch {
        // Keep fallback destination name if reverse geocoding fails.
      }
    };

    resolveDestinationName();

    return () => {
      cancelled = true;
    };
  }, [
    mapsReady,
    focusedDestination,
    focusedDestinationName
  ]);

  useEffect(() => {
    if (!tripMap || !mapsReady) return;

    const points = [focusedOrigin, focusedDestination].filter(Boolean);
    if (!points.length) return;

    if (points.length === 1) {
      tripMap.setCenter(points[0]);
      tripMap.setZoom(14);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    tripMap.fitBounds(bounds, 56);
  }, [
    tripMap,
    mapsReady,
    selectedStudentId,
    focusedOrigin,
    focusedDestination
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00E0E0] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading parent dashboard...</p>
        </div>
      </div>
    );
  }

  if (!parentProfile) {
    return (
      <div className="p-8">
        <div className="max-w-3xl mx-auto bg-white rounded-xl shadow p-8">
          <h1 className="text-2xl font-bold text-[#1a1f2e] mb-3">Parent account pending activation</h1>
          <p className="text-gray-600">
            Your parent profile is not linked to this account yet. Ask your manager/admin to activate your parent
            account from Parents Management.
          </p>
          {error && (
            <div className="mt-4 bg-red-500/10 border border-red-500 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white rounded-xl shadow p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a1f2e]">Parent Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Welcome, {parentProfile.firstName} {parentProfile.lastName}
            </p>
          </div>
        </div>

        {linkageWarning && (
          <div className="bg-yellow-500/10 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
            {linkageWarning}
          </div>
        )}

        {error && <div className="bg-red-500/10 border border-red-500 text-red-600 px-4 py-3 rounded-lg">{error}</div>}

        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <h2 className="text-2xl font-semibold text-[#1a1f2e]">Trips</h2>
            {childTransportCards.length > 1 && (
              <div className="flex items-center gap-3">
                <label htmlFor="child-selector" className="text-sm text-gray-600">Child</label>
                <select
                  id="child-selector"
                  value={selectedStudentId}
                  onChange={(event) => setSelectedStudentId(event.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#00E0E0]"
                >
                  {childTransportCards.map((card) => (
                    <option key={card.student.id} value={card.student.id}>
                      {card.student.firstName} {card.student.lastName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {!hasActiveTrip ? (
            <div className="mt-4">
              <div className="text-gray-600">No active trips</div>
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase text-gray-500">Trip</div>
                <div className="text-base font-semibold text-[#1a1f2e]">
                  {focusedCard.tripLabel} - {displayedDestinationName}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">ETA</div>
                <div className="text-base font-semibold text-[#1a1f2e]">
                  {displayedEtaMinutes !== null ? `${displayedEtaMinutes} mins` : 'N/A'}
                </div>
                {displayedDistanceText && (
                  <div className="text-xs text-gray-500 mt-1">
                    Distance to drop-off: {displayedDistanceText}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Arrival Time</div>
                <div className="text-base font-semibold text-[#1a1f2e]">
                  {formatArrival(displayedArrivalAt)}
                </div>
              </div>
            </div>
          )}
        </div>

        {hasActiveTrip && (
          <div id="parent-active-trip-map" className="bg-white rounded-xl shadow p-6">
            <h2 className="text-xl font-semibold text-[#1a1f2e] mb-3">
              Track {focusedCard ? `${focusedCard.student.firstName} ${focusedCard.student.lastName}` : 'Child'}'s Trip
            </h2>
            {!focusedCard?.route ? (
              <p className="text-gray-600">No trips scheduled.</p>
            ) : !focusedCard.origin || !focusedCard.destination ? (
              <p className="text-gray-600">Trip route geometry is unavailable for this child.</p>
            ) : mapsLoadError ? (
              <p className="text-gray-600">Unable to load Google Maps right now.</p>
            ) : !isMapsLoaded ? (
              <p className="text-gray-600">Loading map...</p>
            ) : (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={mapCenter}
                options={googleMapOptions}
                onLoad={handleTripMapLoad}
                onUnmount={handleTripMapUnmount}
              >
                {focusedDirections && (
                  <DirectionsRenderer
                    directions={focusedDirections}
                    options={directionsRendererOptions}
                  />
                )}
                {mapsReady && focusedOrigin && (
                  useAdvancedMarkers ? (
                    <AdvancedMapMarker
                      map={tripMap}
                      position={focusedOrigin}
                      title="Start Location"
                      buildContent={buildOriginMarkerContent}
                    />
                  ) : (
                    <Marker
                      position={focusedOrigin}
                      title="Start Location"
                      icon={{
                        path: circleMarkerPath,
                        scale: 10,
                        fillColor: '#34D399',
                        fillOpacity: 1,
                        strokeWeight: 2,
                        strokeColor: '#1B9A59'
                      }}
                    />
                  )
                )}
                {mapsReady && focusedDestination && (
                  useAdvancedMarkers ? (
                    <AdvancedMapMarker
                      map={tripMap}
                      position={focusedDestination}
                      title="Drop-off Location"
                      buildContent={buildDestMarkerContent}
                    />
                  ) : (
                    <Marker
                      position={focusedDestination}
                      title="Drop-off Location"
                      icon={{
                        path: circleMarkerPath,
                        scale: 10,
                        fillColor: '#EF4444',
                        fillOpacity: 1,
                        strokeWeight: 2,
                        strokeColor: '#B91C1C'
                      }}
                    />
                  )
                )}
                {mapsReady && focusedLiveLocation && (
                  useAdvancedMarkers ? (
                    <AdvancedMapMarker
                      map={tripMap}
                      position={focusedLiveLocation}
                      title={focusedStudentName || 'Bus'}
                      buildContent={buildBusMarkerContent}
                    />
                  ) : (
                    <Marker
                      position={focusedLiveLocation}
                      icon={createBusIcon(32)}
                      label={{
                        text: focusedCard?.student?.firstName || 'Bus',
                        color: '#111827',
                        fontSize: '12px',
                        fontWeight: '700'
                      }}
                      title={focusedStudentName || 'Bus'}
                    />
                  )
                )}
              </GoogleMap>
            )}
          </div>
        )}

        {hasActiveTrip && (
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="text-xl font-semibold text-[#1a1f2e] mb-3">Bus Details</h2>
            {!focusedCard?.route ? (
              <p className="text-gray-600">No trip details available.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col gap-3">
                <div className="text-xs uppercase text-gray-500">Driver</div>
                <div className="flex items-center gap-3">
                  {resolvedDriverPhoto ? (
                    <img
                      src={resolvedDriverPhoto}
                      alt={resolvedDriverName}
                      className="w-12 h-12 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#e5e7eb] text-gray-700 flex items-center justify-center font-semibold">
                      {resolvedDriverInitial}
                    </div>
                  )}
                  <div className="text-sm font-semibold text-[#1a1f2e]">
                    {resolvedDriverName}
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Assistant:</span> {focusedCard.bus?.assignedAssistant?.name || 'Not assigned'}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="text-xs uppercase text-gray-500">Bus</div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Registration:</span> {focusedCard.bus?.registrationNumber || 'N/A'}
                </div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Fleet Number:</span> {focusedCard.bus?.fleetNumber || 'N/A'}
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <div className="text-xs uppercase text-gray-500">Live Metrics</div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Current Speed:</span> {focusedCard.speedKph} km/h
                </div>
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Live Location:</span> {focusedCard.hasLiveLocation ? 'Available' : 'Unavailable'}
                </div>
              </div>
            </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold text-[#1a1f2e]">Upcoming Trips</h2>
          <p className="text-sm text-gray-500 mt-1">
            {focusedCard
              ? `${focusedCard.student.firstName} ${focusedCard.student.lastName}`
              : 'Selected child'}
          </p>

          {!upcomingTripsForFocusedChild.length ? (
            <div className="mt-4 text-gray-600">No upcoming trips for this child.</div>
          ) : (
            <div className="mt-4 space-y-3">
              {upcomingTripsForFocusedChild.map((trip) => (
                <div key={trip.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="text-sm font-semibold text-[#1a1f2e]">
                    {trip.tripLabel} - {trip.destinationName}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Date: {formatTripDate(trip.tripDate)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Route: {trip.routeName}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Departure: {formatDeparture(trip.departureAt)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentDashboard;
